import * as React from 'react';
import { createUseStyles } from 'react-jss';
import ThemeObject, { Theme } from 'helpers/theme';
import classNames from 'classnames';
import Icon from '../../../../sharedComponents/core/Icon';
import AnchoredPanel from 'sharedComponents/core/AnchoredPanel';
import { ScaleLoader } from 'react-spinners';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons';
import { faMagnifyingGlass } from '@fortawesome/pro-regular-svg-icons';

const useStyles = createUseStyles((theme: Theme) => ({
  tagContainer: {
    width: '100%'
  },
  tag: {
    height: 40,
    display: 'inline-flex',
    alignItems: 'center',
    flexDirection: 'row',
    backgroundColor: '#f8f9fb',
    border: '1px solid #ededed',
    borderRadius: 8,
    fontSize: theme.fontSizes.default,
    padding: [0, 15],
    margin: [0, 15, 15, 0]
  },
  removeTag: {
    margin: [0, 0, 5, 15],
    height: 20,
    width: 12
  },
  container: {
    width: '100%',
    height: 40,
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'strech'
  },
  clickableBox: {
    height: 40,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: [0, 14],
    border: '1px solid #ededed',
    borderRadius: 8,
    cursor: 'pointer',
    transition: 'border-color 0.15s',
    '&:hover': {
      borderColor: '#d7dae0'
    }
  },
  boxChevron: {
    fontSize: 11,
    color: '#74777f',
    transition: 'transform 0.15s ease-out'
  },
  dropdown: {
    backgroundColor: 'white',
    maxHeight: 450, // don't know how this should be calculated
    border: '1px solid #ededed',
    borderRadius: 10,
    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.06)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    zIndex: 2
  },
  searchContainer: {
    padding: 10
  },
  searchRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '0 14px',
    minHeight: 40,
    background: 'white',
    border: '1px solid #ededed',
    borderRadius: 8,
    transition: 'border-color 0.15s, box-shadow 0.15s',
    '&:focus-within': {
      borderColor: '#0b57ff',
      boxShadow: '0 0 0 3px rgba(11, 87, 255, 0.1)'
    }
  },
  searchIcon: {
    color: '#9ea2ad',
    fontSize: 14
  },
  searchInput: {
    flex: 1,
    marginLeft: 12,
    border: 'none',
    outline: 'none',
    fontSize: 15,
    color: theme.colors.primaryBlack,
    '&::placeholder': {
      color: '#9ea2ad'
    }
  },
  tempResults: {
    height: 100,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderTop: '1px solid #ededed'
  },
  tempPlaceholder: {
    color: '#74777f'
  },
  searchResults: {
    overflowY: 'auto'
  },
  group: {
    borderTop: '1px solid #ededed'
  },
  headerRow: {
    height: 42,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: [0, 14],
    cursor: 'pointer',
    backgroundColor: '#f0f1f3',
    transition: 'background-color 0.15s',
    '&:hover': {
      backgroundColor: '#e6e8ed'
    }
  },
  headerChevron: {
    fontSize: 10,
    color: '#9ea2ad',
    marginRight: 10,
    transition: 'transform 0.15s ease-out'
  },
  headerTitle: {
    fontSize: 13,
    fontWeight: 600,
    color: theme.colors.primaryBlack
  },
  role: {
    padding: '10px 14px 4px',
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    color: '#9ea2ad'
  },
  courseRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gridGap: 12,
    padding: '9px 14px',
    cursor: 'pointer',
    transition: 'background-color 0.15s',
    '&:hover': {
      backgroundColor: '#f8f9fb'
    }
  },
  courseName: {
    display: 'flex',
    alignItems: 'center',
    fontSize: 13,
    fontWeight: 500,
    color: theme.colors.primaryBlack
  },
  coursePrice: {
    flexShrink: 0,
    fontSize: 13,
    color: '#74777f'
  },
  pillClassroom: {
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: 0.4,
    textTransform: 'uppercase',
    color: '#0b57ff',
    backgroundColor: '#eef3ff',
    borderRadius: 999,
    padding: [3, 9]
  },
  pillOnline: {
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: 0.4,
    textTransform: 'uppercase',
    color: '#74777f',
    backgroundColor: '#f0f1f3',
    borderRadius: 999,
    padding: [3, 9]
  },
  checkbox: {
    marginRight: 15
  },
  placeholderText: {
    fontSize: theme.fontSizes.small,
    fontWeight: 400,
    color: theme.colors.primaryBlack
  },
  leftItems: {
    display: 'flex',
    alignItems: 'center'
  }
}));

export type CourseCategory = {
  title: string;
  courses: Course[];
};

export type Course = {
  id: number | string; // not sure yet
  name: string;
  price: number;
  role: string;
  backgroundCheck: boolean;
  specificTerms: string;
  category?: {
    uuid: string | null;
  };
  secondaryCategory?: {
    uuid: string | null;
  };
};

export type Category = {
  uuid: string;
  name: string;
  secondaryCategories: SecondaryCategory[];
  classroomOnly: boolean;
};

export type SecondaryCategory = {
  uuid: string;
  name: string;
  hasCourses?: boolean;
};

const NO_CATEGORY_ID = 'no-category';

function composeDropdownID(
  categoryUUID?: string,
  secondaryCategoryUUID?: string
) {
  if (secondaryCategoryUUID && categoryUUID) {
    return `${categoryUUID}:${secondaryCategoryUUID}`;
  }
  if (categoryUUID) {
    return categoryUUID;
  }

  return NO_CATEGORY_ID;
}

function explodeDropdownID(dropdownID: string) {
  if (dropdownID === NO_CATEGORY_ID) {
    return {};
  }
  const decomposedID = dropdownID.split(':');
  switch (decomposedID.length) {
    case 1:
      return {
        categoryUUID: decomposedID[0]
      };
      break;
    case 2:
      return {
        categoryUUID: decomposedID[0],
        secondaryCategoryUUID: decomposedID[1]
      };
      break;
    default:
      throw new Error('Unable to decompose dropdown ID');
      break;
  }
}

type DropdownState = {
  courses: Course[];
  dropped: boolean;
  hidden: boolean;
  name: string;
  classroomOnly: boolean;
  loading: boolean;
};

type ReducerState = {
  [key: string]: DropdownState;
};

type ActionType =
  | 'set-categories'
  | 'set-dropdown-courses'
  | 'set-all-courses'
  | 'set-dropdown-loading';
type Action = {
  type: ActionType;
  categories?: Category[];
  dropdownID?: string;
  dropped?: boolean;
  courses?: Course[];
  loading?: boolean;
};

function dropdownReducer(state: ReducerState, action: Action) {
  switch (action.type) {
    case 'set-categories':
      if (!action.categories) return;
      const newDropdowns = {};
      action.categories.forEach((category) => {
        if (
          category.secondaryCategories &&
          category.secondaryCategories.length > 0
        ) {
          category.secondaryCategories.forEach((secondary) => {
            if (secondary.hasCourses === false) {
              return;
            }
            newDropdowns[composeDropdownID(category.uuid, secondary.uuid)] = {
              dropped: false,
              name: `${category.name} - ${secondary.name}`,
              classroomOnly: category.classroomOnly,
              courses: []
            };
          });
        } else {
          newDropdowns[composeDropdownID(category.uuid, undefined)] = {
            dropped: false,
            name: category.name,
            classroomOnly: category.classroomOnly,
            courses: []
          };
        }
      });
      return newDropdowns;
    case 'set-dropdown-courses':
      if (!action.dropdownID)
        throw new Error('dropdownID required to update courses');
      return {
        ...state,
        [action.dropdownID]: {
          ...state[action.dropdownID],
          courses: action.courses,
          dropped: action.dropped
        }
      };
    case 'set-dropdown-loading':
      if (!action.dropdownID)
        throw new Error('dropdownID required to update loading');

      return {
        ...state,
        [action.dropdownID]: {
          ...state[action.dropdownID],
          loading: action.loading
        }
      };
    case 'set-all-courses':
      // Search through results putting them in the right dropdown
      if (!action.courses) {
        throw new Error("courses required for dispatch 'set-all-courses'");
        return;
      }
      const initialState = { ...state };
      const newState = {};

      for (const [key, dropdownItem] of Object.entries(initialState)) {
        newState[key] = {
          ...dropdownItem,
          hidden: true,
          courses: []
        };
      }
      action.courses.map((course) => {
        const dropdownID = composeDropdownID(
          course?.category?.uuid ?? undefined,
          course?.secondaryCategory?.uuid ?? undefined
        );
        if (initialState[dropdownID]) {
          newState[dropdownID] = {
            ...initialState[dropdownID],
            courses: [...(newState[dropdownID]?.courses ?? []), course],
            hidden: false
          };
        }
      });
      return newState;
  }
}

type Props = {
  multiselect?: boolean;
  selected: Course[];
  categories: Category[];
  setSelected: (selected: Course[]) => void;
  searchQuery: (
    categoryUUID?: string,
    secondaryCategoryUUID?: string,
    text?: string
  ) => Promise<Course[]>;
  debounceTime?: number;
};

const roles = ['Operatives', 'Supervisors', 'Managers'];

function SearchableDropdown({
  multiselect,
  selected = [],
  categories,
  setSelected,
  searchQuery,
  debounceTime = 600
}: Props) {
  const classes = useStyles();
  const [isOpen, setOpen] = React.useState<boolean>(false);
  const [isLoading, setLoading] = React.useState<boolean>(false);
  const [search, setSearch] = React.useState<string>('');
  const [currentRole, setcurrentRole] = React.useState<string>('Supervisors');
  const [dropdowns, dispatch]: [
    ReducerState | undefined,
    React.Dispatch<Action>
  ] = React.useReducer(dropdownReducer, {});
  const searchRef = React.useRef<HTMLInputElement>(null);
  const debounceRef = React.useRef<number | undefined>();
  const containerRef = React.useRef<HTMLDivElement>(null);
  const panelRef = React.useRef<HTMLDivElement>(null);

  // Close the dropdown when clicking anywhere outside it. The panel is
  // portaled to document.body, so check it separately.
  React.useEffect(() => {
    if (!isOpen) return;
    const onMouseDown = (evt: MouseEvent) => {
      const target = evt.target as Node;
      if (
        containerRef.current &&
        !containerRef.current.contains(target) &&
        !panelRef.current?.contains(target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [isOpen]);

  const placeholder = multiselect
    ? 'Please select one or more Courses'
    : selected.length > 0
    ? selected[0].name
    : 'Please select the Course you wish to book';

  React.useEffect(() => {
    dispatch({
      type: 'set-categories',
      categories
    });
  }, [categories]);

  React.useEffect(() => {
    if (isOpen && searchRef && searchRef.current) {
      searchRef.current.focus();
    }
  }, [isOpen]);

  React.useEffect(() => {
    if (search === '') {
      dispatch({
        type: 'set-categories',
        categories
      });
      setLoading(false);
      return;
    }

    setLoading(true);
    clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(async () => {
      const results = await searchQuery(undefined, undefined, search);
      // Search through results putting them in the right dropdown
      dispatch({
        type: 'set-all-courses',
        courses: results
      });
      setLoading(false);
    }, debounceTime);

    return () => {
      clearTimeout(debounceRef.current);
    };
  }, [search, debounceTime, searchQuery]);

  return (
    <>
      {multiselect && selected.length > 0 && (
        <div className={classes.tagContainer}>
          {selected.map(({ name, id }: Course) => (
            <div className={classes.tag} key={id}>
              {name}
              <Icon
                pointer
                name="RemoveSelectedCourse_X"
                className={classes.removeTag}
                size={null} // set in class
                onClick={() => setSelected(selected.filter((f) => f.id !== id))}
              />
            </div>
          ))}
        </div>
      )}
      <div className={classes.container} ref={containerRef}>
        <div
          className={classes.clickableBox}
          onClick={() => setOpen((o) => !o)}
        >
          <p className={classes.placeholderText}>{placeholder}</p>
          <FontAwesomeIcon
            icon={faChevronDown}
            className={classes.boxChevron}
            style={{ transform: isOpen ? 'rotate(180deg)' : 'none' }}
          />
        </div>
        <AnchoredPanel
          anchorRef={containerRef}
          open={isOpen}
          offset={8}
          panelRef={panelRef}
        >
          <div className={classes.dropdown}>
            <div className={classes.searchContainer}>
              <div className={classes.searchRow}>
                <FontAwesomeIcon
                  icon={faMagnifyingGlass}
                  className={classes.searchIcon}
                />
                <input
                  ref={searchRef}
                  className={classes.searchInput}
                  placeholder="Search courses"
                  value={search}
                  onChange={(evt) => setSearch(evt.target.value)}
                />
              </div>
            </div>
            {isLoading ? (
              <div className={classes.tempResults}>
                <ScaleLoader
                  color={ThemeObject.colors.borderGrey}
                  height={20}
                  loading={isLoading}
                />
              </div>
            ) : categories.length === 0 ? (
              <div className={classes.tempResults}>
                <p className={classes.tempPlaceholder}>No Courses found</p>
              </div>
            ) : (
              <div className={classes.searchResults}>
                {dropdowns &&
                  Object.entries(dropdowns)
                    .sort((a, b) => (a[1].classroomOnly == false ? 1 : -1))
                    .map(
                      (
                        [
                          dropdownID,
                          {
                            name,
                            courses,
                            dropped,
                            hidden,
                            classroomOnly,
                            loading
                          }
                        ],
                        index
                      ) =>
                        hidden ? null : (
                          <div key={dropdownID} className={classes.group}>
                            <div
                              className={classes.headerRow}
                              onClick={async () => {
                                dispatch({
                                  type: 'set-dropdown-loading',
                                  dropdownID,
                                  loading: true
                                });

                                const idents = explodeDropdownID(dropdownID);
                                const courses = await searchQuery(
                                  idents.categoryUUID,
                                  idents.secondaryCategoryUUID,
                                  search
                                );

                                dispatch({
                                  type: 'set-dropdown-courses',
                                  courses: courses,
                                  dropdownID: dropdownID,
                                  dropped: !dropped
                                });
                                dispatch({
                                  type: 'set-dropdown-loading',
                                  dropdownID,
                                  loading: false
                                });
                              }}
                            >
                              <span className={classes.leftItems}>
                                <FontAwesomeIcon
                                  icon={faChevronDown}
                                  className={classes.headerChevron}
                                  style={{
                                    transform: dropped
                                      ? 'rotate(180deg)'
                                      : 'none'
                                  }}
                                />
                                <span className={classes.headerTitle}>
                                  {name}
                                </span>
                              </span>
                              <span
                                className={
                                  classroomOnly
                                    ? classes.pillClassroom
                                    : classes.pillOnline
                                }
                              >
                                {classroomOnly ? 'Classroom' : 'Online'}
                              </span>
                            </div>
                            {courses
                              .sort(
                                (a, b) =>
                                  roles.indexOf(a.role) - roles.indexOf(b.role)
                              )
                              .map(
                                (
                                  {
                                    name,
                                    id,
                                    price,
                                    role,
                                    backgroundCheck,
                                    specificTerms
                                  }: Course,
                                  index
                                ) => {
                                  return dropped ? (
                                    <div>
                                      {(index != 0 &&
                                        courses[index - 1].role) != role && (
                                        <div className={classes.role}>
                                          {role}
                                        </div>
                                      )}
                                      <div
                                        key={id}
                                        className={classes.courseRow}
                                        onClick={() => {
                                          if (multiselect) {
                                            if (
                                              selected
                                                .map((course) => course.id)
                                                .includes(id)
                                            ) {
                                              setSelected(
                                                selected.filter(
                                                  (f) => f.id !== id
                                                )
                                              );
                                            } else {
                                              setSelected([
                                                ...selected,
                                                {
                                                  name,
                                                  id,
                                                  price,
                                                  backgroundCheck,
                                                  specificTerms,
                                                  role
                                                }
                                              ]);
                                            }
                                          } else {
                                            setSelected([
                                              {
                                                name,
                                                id,
                                                price,
                                                backgroundCheck,
                                                specificTerms,
                                                role
                                              }
                                            ]);
                                            setSearch('');
                                            setOpen(false);
                                          }
                                        }}
                                      >
                                        <span className={classes.courseName}>
                                          {multiselect &&
                                            (selected
                                              .map((course) => course.id)
                                              .includes(id) ? (
                                              <Icon
                                                name="FormCheckbox_Checked"
                                                className={classes.checkbox}
                                                size={17}
                                              />
                                            ) : (
                                              <Icon
                                                name="FormCheckbox_Unchecked"
                                                className={classes.checkbox}
                                                size={17}
                                              />
                                            ))}
                                          {name}
                                        </span>
                                        <span className={classes.coursePrice}>
                                          £{price.toFixed(2)}
                                        </span>
                                      </div>
                                    </div>
                                  ) : null;
                                }
                              )}
                          </div>
                        )
                    )}
              </div>
            )}
          </div>
        </AnchoredPanel>
      </div>
    </>
  );
}

export default SearchableDropdown;
